import { useOptionalAnalytics } from "@/src/sb/components/Analytics"
import { AccountDetails } from "@/src/sb/core/validatorSchemas/profile-setup"
import classNames from "classnames"
import { ReactNode, useEffect, useRef, useState } from "react"

import AccountDetailsStep, { EMPTY_ACCOUNT_DETAILS } from "./AccountDetailsStep"
import DataPlatformStep, {
  DataPlatformSelection,
  EMPTY_DATA_PLATFORM_SELECTION
} from "./DataPlatformStep"
import StepProgress from "./StepProgress"
import UseCaseStep, {
  EMPTY_USE_CASE_SELECTION,
  UseCaseSelection
} from "./UseCaseStep"
import { buildOnboardingAttributes } from "./onboardingAttributes"
import {
  ParsedProfileApiError,
  ProfileApiErrorField,
  parseProfileApiError
} from "./profileApiErrors"
import {
  DRAFT_STORAGE_PREFIX,
  useProfileSetupDraft
} from "./useProfileSetupDraft"

export const TOTAL_STEPS = 3

const STEP_BY_FIELD: Record<ProfileApiErrorField, number> = {
  role: 1,
  team: 1,
  phoneNumber: 1,
  city: 1,
  country: 1,
  companyName: 1,
  industry: 1,
  companySize: 1,
  referralSource: 1,
  useCases: 2,
  platforms: 3
}

export type ProfileSetupData = AccountDetails &
  UseCaseSelection &
  DataPlatformSelection

const EMPTY_PROFILE_SETUP: ProfileSetupData = {
  ...EMPTY_ACCOUNT_DETAILS,
  ...EMPTY_USE_CASE_SELECTION,
  ...EMPTY_DATA_PLATFORM_SELECTION
}

type StepCopy = {
  title: string
  subtitle: string
  width: string
}

const STEP_COPY: Record<number, StepCopy> = {
  1: {
    title: "Set up your account",
    subtitle: "Complete your profile to get started.",
    width: "max-w-[368px]"
  },
  2: {
    title: "What do you plan to use Stears for?",
    subtitle:
      "This helps us make sure you get exactly what you need. Select all that apply.",
    width: "max-w-[612px]"
  },
  3: {
    title: "What other data platforms do you already use?",
    subtitle: "Select all that apply",
    width: "max-w-[612px]"
  }
}

type ProfileSetupWizardProps = {
  onComplete: (data: ProfileSetupData) => void | Promise<void>
  storageKey?: string
  includeOrganisation?: boolean
}

const ProfileSetupWizard = ({
  onComplete,
  storageKey = DRAFT_STORAGE_PREFIX,
  includeOrganisation = false
}: ProfileSetupWizardProps) => {
  const analytics = useOptionalAnalytics()
  const { draft, restored, save, clear } =
    useProfileSetupDraft<ProfileSetupData>(storageKey)
  const startReported = useRef(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [data, setData] = useState<ProfileSetupData>(EMPTY_PROFILE_SETUP)
  const [hydrated, setHydrated] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<ParsedProfileApiError | null>(
    null
  )

  useEffect(() => {
    if (!restored) return
    if (draft) {
      setData({ ...EMPTY_PROFILE_SETUP, ...draft.data })
      setCurrentStep(Math.min(Math.max(1, draft.step ?? 1), TOTAL_STEPS))
    }
    setHydrated(true)
  }, [restored, draft])

  useEffect(() => {
    if (!hydrated) return
    save({ step: currentStep, data })
  }, [hydrated, currentStep, data, save])

  useEffect(() => {
    if (!hydrated || startReported.current) return
    startReported.current = true
    analytics?.trackOnboardingStart({
      flow: "signup",
      step: currentStep,
      resumed: Boolean(draft)
    })
  }, [hydrated, currentStep, draft, analytics])

  const collect = (values: Partial<ProfileSetupData>) =>
    setData((current) => ({ ...current, ...values }))

  const stepOwningFirstError = (error: ParsedProfileApiError) => {
    const steps = (
      Object.keys(error.fieldErrors) as Array<ProfileApiErrorField>
    )
      .map((field) => STEP_BY_FIELD[field])
      .filter(Boolean)

    return steps.length ? Math.min(...steps) : null
  }

  const goToNextStep = async (values: Partial<ProfileSetupData>) => {
    const updated = { ...data, ...values }
    setData(updated)

    if (currentStep === TOTAL_STEPS) {
      setSubmitError(null)
      setSubmitting(true)
      try {
        await onComplete(updated)
        analytics?.trackOnboardingComplete({
          flow: "signup",
          attributes: buildOnboardingAttributes(updated, includeOrganisation)
        })
        clear()
      } catch (error) {
        const parsed = parseProfileApiError(error)
        setSubmitError(parsed)
        setSubmitting(false)

        const step = stepOwningFirstError(parsed)
        if (step) setCurrentStep(step)
      }
      return
    }

    setSubmitError(null)
    setCurrentStep((step) => step + 1)
  }

  const goToPreviousStep = () => {
    setSubmitError(null)
    setCurrentStep((step) => Math.max(1, step - 1))
  }

  const steps: Record<number, ReactNode> = {
    1: (
      <AccountDetailsStep
        initialValues={data}
        onSubmit={goToNextStep}
        onChange={collect}
        includeOrganisation={includeOrganisation}
        serverErrors={submitError?.fieldErrors}
      />
    ),
    2: (
      <UseCaseStep
        initialValues={data}
        onSubmit={goToNextStep}
        onBack={goToPreviousStep}
        onChange={collect}
        error={submitError?.fieldErrors.useCases}
      />
    ),
    3: (
      <DataPlatformStep
        initialValues={data}
        onSubmit={goToNextStep}
        onBack={goToPreviousStep}
        onChange={collect}
        submitting={submitting}
        error={submitError?.fieldErrors.platforms}
      />
    )
  }

  const { title, subtitle, width } = STEP_COPY[currentStep]

  if (!hydrated) return null

  return (
    <section
      className={classNames(
        "mx-auto flex w-full flex-col gap-5 text-left font-jakarta",
        width
      )}
    >
      <StepProgress currentStep={currentStep} totalSteps={TOTAL_STEPS} />

      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold dark:text-stears_white">{title}</h1>
        <p className="text-xs tracking-[-0.01em] dark:text-stears_white">
          {subtitle}
        </p>
      </header>

      {submitError?.formErrors.length ? (
        <div role="alert" className="flex flex-col gap-1 p-2 text-xs">
          {submitError.formErrors.map((message) => (
            <p key={message} className="text-[#F05042]">
              {message}
            </p>
          ))}
        </div>
      ) : null}

      <main>{steps[currentStep]}</main>
    </section>
  )
}

export default ProfileSetupWizard
